import * as Sentry from '@sentry/react'
import { useLayoutEffect } from 'react'
import { Outlet } from 'react-router'
import { axiosPrivate } from '@/shared/api/axios'
import { HttpStatus } from '@/shared/constants/http-status'
import useAuth from '../hooks/use-auth'
import useRefreshToken from '../hooks/use-refresh-token'

const AxiosInterceptor = () => {
  const { auth } = useAuth()
  const refresh = useRefreshToken()

  useLayoutEffect(() => {
    const requestIntercept = axiosPrivate.interceptors.request.use(
      (config) => {
        if (!config.headers.Authorization && auth?.accessToken) {
          config.headers.Authorization = `Bearer ${auth?.accessToken}`
        }
        return config
      },
      async (error) => await Promise.reject(error)
    )

    const responseIntercept = axiosPrivate.interceptors.response.use(
      (response) => response,
      async (error) => {
        const prevRequest = error?.config
        if (error?.response?.status === HttpStatus.UNAUTHORIZED && !prevRequest?.sent) {
          prevRequest.sent = true
          const newAccessToken = await refresh()
          if (!newAccessToken) return await Promise.reject(error)
          prevRequest.headers.Authorization = `Bearer ${newAccessToken}`
          return await axiosPrivate(prevRequest)
        }

        // Capture 5xx server errors in Sentry — 4xx are expected business logic
        if (error?.response?.status >= 500) {
          Sentry.withScope((scope) => {
            scope.setExtra('url', error.config?.url)
            scope.setExtra('method', error.config?.method)
            scope.setExtra('status', error.response?.status)
            scope.setExtra(
              'errorCode',
              error.response?.data?.code ?? error.response?.data?.errorCode
            )
            scope.setExtra('requestId', error.response?.data?.requestId)
            Sentry.captureException(error)
          })
        }

        return await Promise.reject(error)
      }
    )

    return () => {
      axiosPrivate.interceptors.request.eject(requestIntercept)
      axiosPrivate.interceptors.response.eject(responseIntercept)
    }
  }, [auth?.accessToken, refresh])

  return <Outlet />
}

export default AxiosInterceptor
